/**
 * Role Helper Utilities
 * Core role constants and utilities for the application
 */

export const SUPER_ADMIN_ROLE_ID = 1;

/**
 * Minimal static hierarchy for fallback only
 * Used only when role_order is unavailable (should rarely happen)
 * @deprecated Use role_order from database instead. This is fallback only.
 */
export const ROLE_HIERARCHY = Object.freeze([SUPER_ADMIN_ROLE_ID]);

/**
 * Cached rank lookup by role ID for quick comparisons (fallback only)
 */
const ROLE_RANK_BY_ID = ROLE_HIERARCHY.reduce((acc, roleId, index) => {
  acc[roleId] = index;
  return acc;
}, {});

/**
 * Returns hierarchy rank for the provided role ID
 * Lower index => lower authority
 * @param {number|string} roleId - Role ID to get rank for
 * @returns {number|null} Rank index or null if invalid
 */
export const getRoleRankById = (roleId) => {
  const numericId = Number(roleId);

  if (!Number.isFinite(numericId)) {
    return null;
  }

  return ROLE_RANK_BY_ID[numericId] ?? null;
};

/**
 * Determines whether the actor role has higher authority than the target role
 * @param {number|string} actorRoleId - Role ID of the user performing the action
 * @param {number|string} targetRoleId - Role ID being acted upon
 * @returns {boolean} True if actor has higher authority than target
 */
export const canManageRoleId = (actorRoleId, targetRoleId) => {
  const actorRank = getRoleRankById(actorRoleId);
  const targetRank = getRoleRankById(targetRoleId);

  if (actorRank === null || targetRank === null) {
    return false;
  }

  return actorRank > targetRank;
};

/**
 * Get role order from role object
 * Higher order number = higher authority (right side of hierarchy)
 * Lower order number = lower authority (left side of hierarchy)
 * @param {Object} role - Role object with order property
 * @returns {number|null} Role order value or null if invalid
 */
export const getRoleOrder = (role) => {
  if (!role || role.order === undefined || role.order === null) {
    return null;
  }
  const order = Number(role.order);
  return Number.isFinite(order) ? order : null;
};

/**
 * Determines if the current user can assign the target role based on dynamic hierarchy
 * Uses order field: user can assign roles with higher order (roles with lower authority)
 * @param {Object} currentUser - User performing the action (should have role_id and role_order)
 * @param {number|string} targetRoleId - Role ID being assigned
 * @param {Array} allRoles - Array of all role objects with order field (optional, for dynamic check)
 * @returns {boolean} True if user can assign the target role
 */
export const canAssignRole = (currentUser, targetRoleId, allRoles = []) => {
  const roleId = Number(targetRoleId);
  const currentRoleId = Number(currentUser?.role_id);

  if (!Number.isFinite(roleId) || !Number.isFinite(currentRoleId)) {
    return false;
  }

  // Always prefer role_order from user object first, then from allRoles
  let currentOrder = null;

  // Try to get current user's role_order from user object
  if (
    currentUser?.role_order !== undefined &&
    currentUser?.role_order !== null
  ) {
    currentOrder = Number(currentUser.role_order);
    if (!Number.isFinite(currentOrder)) {
      currentOrder = null;
    }
  }

  // If allRoles array is provided, use dynamic order-based hierarchy
  if (allRoles && allRoles.length > 0) {
    const currentUserRole = allRoles.find(
      (r) => Number(r.id) === currentRoleId
    );
    const targetRole = allRoles.find((r) => Number(r.id) === roleId);

    if (currentUserRole && targetRole) {
      // Use role_order from user object if available, otherwise from role object
      if (currentOrder === null) {
        currentOrder = getRoleOrder(currentUserRole);
      }
      const targetOrder = getRoleOrder(targetRole);

      if (currentOrder !== null && targetOrder !== null) {
        // User can assign roles with higher order number (roles below in hierarchy)
        return targetOrder > currentOrder;
      }
    }
  }

  return canManageRoleId(currentRoleId, roleId);
};

/**
 * Determines if the current user can manage the target user based on role_order
 * A user can only manage users whose roles have a higher role_order value (lower privilege)
 * @param {Object} currentUser - Current user object (should have role_id and role_order)
 * @param {Object} targetUser - Target user object (should have role_id and role_order)
 * @param {Array} allRoles - Array of all role objects with order field (optional, for dynamic check)
 * @returns {boolean} True if current user can manage target user
 */
export const canManageUser = (currentUser, targetUser, allRoles = []) => {
  if (!currentUser || !targetUser) {
    return false;
  }

  const targetRoleId = Number(targetUser?.role_id);
  if (!Number.isFinite(targetRoleId)) {
    return false;
  }

  // Use canAssignRole to check if current user can assign target user's role
  return canAssignRole(currentUser, targetRoleId, allRoles);
};

/**
 * Check if user has specific permission
 * @param {Object} user - User object with permissions property
 * @param {string} module - Module name (dashboard, users, companies, incentive, reports)
 * @param {string} action - Action name (view, create, update, delete, reset_password, view_all_companies, export_csv, edit_leads)
 * @returns {boolean} True if user has the specified permission
 */
export const hasPermission = (user, module, action) => {
  if (!user || !user.permissions) {
    return false;
  }

  return user.permissions[module]?.[action] === true;
};

/**
 * Check if user can view all companies
 * @param {Object} user - User object with permissions property
 * @param {string} module - Module name (default: 'dashboard')
 * @returns {boolean} True if user can view all companies
 */
export const canViewAllCompanies = (user, module = "dashboard") => {
  return hasPermission(user, module, "view_all_companies");
};

/**
 * Get allowed companies for user based on their permissions
 * @param {Object} user - User object with permissions and company properties
 * @param {Array} allCompanies - List of all companies
 * @returns {Array} List of companies user can access
 */
export const getAllowedCompanies = (user, allCompanies = []) => {
  if (!user) return [];

  // Super Admin and Admin can see all companies if they have view_all_companies permission
  if (canViewAllCompanies(user, "dashboard")) {
    return allCompanies;
  }

  // Otherwise, only show their own company
  return user.company ? [user.company] : [];
};
